/* jp-search.js — mobile-first predictive search overlay.
   Opens through jp-layers, preserves the shopper's raw Unicode query, and may
   run one additional in-memory professional-terminology query to improve recall.
   Raw queries never enter analytics; recent searches remain consent-gated. */
(() => {
  if (window.__jpSearchInit) return;
  window.__jpSearchInit = true;

  const RECENT_KEY = 'jp_recent_searches';
  const PRODUCT_LIMIT = 5;
  const CONTENT_LIMIT = 3;
  const REQUEST_LIMIT = 6; /* one spare result lets the UI truthfully show 5+ */
  const REQUEST_TIMEOUT = 8000;
  const CACHE_LIMIT = 20;
  const cache = new Map();

  const privacy = () => window.JapanScissors && window.JapanScissors.privacy;
  const getRecent = () => {
    try {
      const store = privacy();
      return JSON.parse((store && store.getPreference(RECENT_KEY)) || '[]');
    } catch { return []; }
  };
  const setRecent = (arr) => {
    const store = privacy();
    if (store) store.setPreference(RECENT_KEY, JSON.stringify(arr.slice(0, 5)));
  };
  const recordRecent = (q) => {
    q = (q || '').trim();
    if (!q) return;
    const recent = getRecent().filter((x) => x.toLocaleLowerCase() !== q.toLocaleLowerCase());
    recent.unshift(q);
    setRecent(recent);
  };

  const esc = (s) => String(s).replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]));
  const escRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const template = (value, token, replacement) => String(value || '').replace(token, replacement);

  /* Improve professional-language recall without replacing the submitted query.
     The raw and canonical forms are fetched together and de-duplicated, so an
     imperfect alias can add a match but can never remove an exact raw match. */
  const intentQuery = (raw) => {
    let query = String(raw || '').normalize('NFKC');
    const rules = [
      [/(^|[\s/,(])(?:vg[\s-]?10|v[\s-]?10)(?=$|[\s/),])/giu, '$1VG-10'],
      [/(^|[\s/,(])ats[\s-]?314(?=$|[\s/),])/giu, '$1ATS-314'],
      [/(^|[\s/,(])(440)(?=$|[\s/),])/giu, '$1$2C'],
      [/(^|[\s/,(])(?:lefty|leftie|left[\s-]?hand)(?=$|[\s/),])/giu, '$1left-handed'],
      [/(^|[\s/,(])shears?(?=$|[\s/),])/giu, '$1scissors'],
      [/(^|[\s/,(])(?:texturizing|texturizer)(?=$|[\s/),])/giu, '$1texturising'],
      [/(^|[\s/,(])thinners?(?=$|[\s/),])/giu, '$1thinning']
    ];
    rules.forEach(([pattern, replacement]) => { query = query.replace(pattern, replacement); });
    return query.replace(/\s+/g, ' ').trim();
  };

  /* Analytics fires only when the customer commits to the full SERP. */
  const trackSubmit = (query) => {
    query = (query || '').trim();
    if (!query) return;
    try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_search_submitted', queryLength: Array.from(query).length } })); } catch {}
  };

  /* Highlight entered/canonical terms in server-rendered text nodes only. */
  const highlight = (root, values) => {
    if (!root) return;
    const seen = new Set();
    const terms = values.flatMap((value) => String(value || '').split(/\s+/u))
      .map((term) => term.trim()).filter((term) => term.length > 1)
      .filter((term) => { const key = term.toLocaleLowerCase(); if (seen.has(key)) return false; seen.add(key); return true; })
      .slice(0, 12);
    if (!terms.length) return;
    const source = '(' + terms.map(escRe).join('|') + ')';
    const test = new RegExp(source, 'iu');
    const split = new RegExp(source, 'giu');
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        const tag = node.parentNode && node.parentNode.nodeName;
        if (tag === 'MARK' || tag === 'SCRIPT' || tag === 'STYLE') return NodeFilter.FILTER_REJECT;
        return test.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });
    const targets = [];
    while (walker.nextNode()) targets.push(walker.currentNode);
    targets.forEach((node) => {
      const holder = document.createElement('span');
      holder.innerHTML = node.nodeValue.split(split)
        .map((part, index) => (index % 2 ? '<mark class="jp-so__mark">' + esc(part) + '</mark>' : esc(part)))
        .join('');
      while (holder.firstChild) node.parentNode.insertBefore(holder.firstChild, node);
      node.parentNode.removeChild(node);
    });
  };

  let overlay;
  let input;
  let clearBtn;
  let emptyEl;
  let liveEl;
  let recentEl;
  let footEl;
  let statusEl;
  let cfg = {};
  let ctrl;
  let timer;
  let timeout;
  let requestSequence = 0;
  let renderSequence = 0;
  let composing = false;

  const init = (root) => {
    if (root.__jpSoBound) return;
    root.__jpSoBound = true;
    overlay = root;
    input = root.querySelector('[data-jp-so-input]');
    clearBtn = root.querySelector('[data-jp-so-clear]');
    emptyEl = root.querySelector('[data-jp-so-empty]');
    liveEl = root.querySelector('[data-jp-so-live]');
    recentEl = root.querySelector('[data-jp-so-recent]');
    footEl = root.querySelector('[data-jp-so-foot]');
    statusEl = root.querySelector('[data-jp-so-status]');
    try { cfg = JSON.parse(root.querySelector('[data-jp-so-config]').textContent); } catch { cfg = {}; }
    cfg.strings = cfg.strings || {};
    if (!input || !clearBtn || !emptyEl || !liveEl) return;

    input.addEventListener('compositionstart', () => { composing = true; window.clearTimeout(timer); });
    input.addEventListener('compositionend', () => { composing = false; onInput(); });
    input.addEventListener('input', onInput);
    input.addEventListener('keydown', onKeydown);

    if (window.visualViewport) {
      const syncViewport = () => { overlay.style.setProperty('--jp-vvh', window.visualViewport.height + 'px'); };
      window.visualViewport.addEventListener('resize', syncViewport);
      document.addEventListener('jp:layer:open', (event) => { if (event.detail && event.detail.id === 'search') syncViewport(); });
    }

    root.addEventListener('click', (event) => {
      const seeAll = event.target.closest('[data-jp-so-seeall]');
      if (seeAll) trackSubmit(seeAll.getAttribute('data-jp-so-seeall') || input.value);
      const setQuery = event.target.closest('[data-jp-soq]');
      if (setQuery) {
        event.preventDefault();
        input.value = setQuery.getAttribute('data-jp-soq') || '';
        clearBtn.hidden = false;
        runSearch();
        input.focus();
        return;
      }
      if (event.target.closest('[data-jp-so-retry]')) { runSearch(); return; }
      if (event.target.closest('[data-jp-so-clear]')) {
        input.value = '';
        clearBtn.hidden = true;
        showEmpty();
        input.focus();
        return;
      }
      if (event.target.closest('[data-jp-so-clear-recent]')) { setRecent([]); renderRecent(); return; }
      if (event.target.closest('[data-jp-so-item]')) recordRecent(input.value);
    });
  };

  const renderRecent = () => {
    const recent = getRecent();
    if (!recent.length) { recentEl.hidden = true; recentEl.innerHTML = ''; return; }
    recentEl.hidden = false;
    recentEl.innerHTML =
      '<div class="jp-so__h">' + esc(cfg.strings.recent || 'Recenti') +
      '<button type="button" class="jp-so__clear-recent" data-jp-so-clear-recent>' + esc(cfg.strings.clearAll || 'Cancella tutto') + '</button></div>' +
      '<div class="jp-so__pills">' +
      recent.map((query) => '<button type="button" class="jp-so__pill" data-jp-soq="' + esc(query) + '">' + esc(query) + '</button>').join('') +
      '</div>';
  };

  const stopRequest = () => {
    window.clearTimeout(timer);
    window.clearTimeout(timeout);
    if (ctrl) ctrl.abort();
    ctrl = null;
  };

  const resetCombobox = () => {
    input.removeAttribute('aria-activedescendant');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', 'jp-so-live');
  };

  const showEmpty = () => {
    stopRequest();
    requestSequence += 1;
    liveEl.hidden = true;
    liveEl.innerHTML = '';
    if (footEl) { footEl.hidden = true; footEl.innerHTML = ''; }
    if (statusEl) statusEl.textContent = '';
    emptyEl.hidden = false;
    renderRecent();
    input.removeAttribute('aria-busy');
    resetCombobox();
  };

  const showLive = () => {
    emptyEl.hidden = true;
    liveEl.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };

  const skeleton = () => {
    showLive();
    input.setAttribute('aria-busy', 'true');
    if (footEl) { footEl.hidden = true; footEl.innerHTML = ''; }
    let markup = '<div class="jp-so__grid jp-so__grid--loading"><div class="jp-so__col">';
    for (let index = 0; index < PRODUCT_LIMIT; index += 1) markup += '<div class="jp-so__skel"><span class="a"></span><span></span></div>';
    markup += '</div></div>';
    liveEl.innerHTML = markup;
    if (statusEl) statusEl.textContent = cfg.strings.loading || '';
  };

  const fullSearchUrl = (query) => (cfg.searchUrl || '/search') + '?q=' + encodeURIComponent(query) + '&type=product&options%5Bprefix%5D=last';

  const noResults = (raw, canonical) => {
    showLive();
    input.removeAttribute('aria-busy');
    resetCombobox();
    if (footEl) { footEl.hidden = true; footEl.innerHTML = ''; }
    const related = canonical && canonical.toLocaleLowerCase() !== raw.toLocaleLowerCase()
      ? '<button type="button" class="jp-so__suggestion" data-jp-soq="' + esc(canonical) + '">' + esc(canonical) + '</button>'
      : '';
    const finder = cfg.finderUrl
      ? '<a class="jp-so__help-link" href="' + esc(cfg.finderUrl) + '">' + esc(cfg.strings.finder || 'Trova le tue forbici') + '<span aria-hidden="true"> →</span></a>'
      : '';
    const contact = cfg.contactUrl
      ? '<a class="jp-so__help-link" href="' + esc(cfg.contactUrl) + '">' + esc(cfg.strings.contact || 'Chiedi a uno specialista') + '<span aria-hidden="true"> →</span></a>'
      : '';
    liveEl.innerHTML =
      '<div class="jp-so__none"><span class="jp-so__none-k" lang="ja" aria-hidden="true">無</span>' +
      '<b>' + esc(template(cfg.strings.noneQuery || cfg.strings.none || 'Nessun risultato.', '[query]', raw)) + '</b>' +
      '<p>' + esc(cfg.strings.noneSub || '') + '</p>' +
      (related ? '<div class="jp-so__suggest"><span>' + esc(cfg.strings.tryInstead || 'Prova una ricerca simile') + '</span>' + related + '</div>' : '') +
      '<div class="jp-so__zero-group"><span class="jp-so__zero-label">' + esc(cfg.strings.browseTypes || cfg.strings.noneBrowse || 'Sfoglia per tipo') + '</span>' +
      '<div class="jp-so__zero-cats">' +
      '<button type="button" data-jp-soq="cutting">' + esc(cfg.strings.catCutting || 'Taglio') + '</button>' +
      '<button type="button" data-jp-soq="thinning">' + esc(cfg.strings.catThinning || 'Sfoltitura') + '</button>' +
      '<button type="button" data-jp-soq="left-handed">' + esc(cfg.strings.catLefty || 'Mancini') + '</button>' +
      '</div></div>' +
      '<div class="row">' +
      '<a class="jp-btn jp-btn--ghost-dark jp-btn--sm" href="' + esc(cfg.allUrl || '/collections/all') + '">' + esc(cfg.strings.noneBrowse || 'Sfoglia tutte le forbici') + '</a>' +
      '<a class="jp-btn jp-btn--primary jp-btn--sm" data-jp-so-seeall="' + esc(raw) + '" href="' + esc(fullSearchUrl(raw)) + '">' + esc(cfg.strings.searchFull || cfg.strings.viewAll || 'Cerca nell’intero catalogo') + '</a>' +
      '</div>' +
      ((finder || contact) ? '<div class="jp-so__help">' + finder + contact + '</div>' : '') +
      '</div>';
    if (statusEl) statusEl.textContent = template(cfg.strings.noneQuery || cfg.strings.none || '', '[query]', raw);
  };

  const searchUnavailable = () => {
    showLive();
    input.removeAttribute('aria-busy');
    resetCombobox();
    if (footEl) { footEl.hidden = true; footEl.innerHTML = ''; }
    liveEl.innerHTML =
      '<div class="jp-so__none jp-so__none--err">' +
      '<span class="jp-so__none-k" lang="ja" aria-hidden="true">再</span>' +
      '<b>' + esc(cfg.strings.errTitle || 'Ricerca non disponibile') + '</b>' +
      '<p>' + esc(cfg.strings.errSub || 'Controlla la connessione e riprova.') + '</p>' +
      '<div class="row"><button type="button" class="jp-btn jp-btn--primary jp-btn--sm" data-jp-so-retry>' + esc(cfg.strings.retry || 'Riprova') + '</button></div></div>';
    if (statusEl) statusEl.textContent = cfg.strings.errTitle || 'Ricerca non disponibile';
  };

  const cacheSet = (key, value) => {
    if (cache.has(key)) cache.delete(key);
    cache.set(key, value);
    if (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
  };

  const fetchPredictive = (query, signal) => {
    const key = query.toLocaleLowerCase();
    if (cache.has(key)) return Promise.resolve(cache.get(key));
    const url = (cfg.predictiveUrl || '/search/suggest') + '?q=' + encodeURIComponent(query) +
      '&section_id=jp-predictive-search' +
      '&resources[type]=product,collection,article,page&resources[limit]=' + REQUEST_LIMIT +
      '&resources[limit_scope]=each&resources[options][unavailable_products]=last';
    return fetch(url, { signal })
      .then((response) => { if (!response.ok) throw new Error('http'); return response.text(); })
      .then((html) => { cacheSet(key, html); return html; });
  };

  const uniqueNodes = (rendered, selector) => {
    const nodes = [];
    const seen = new Set();
    rendered.forEach((root) => {
      root.querySelectorAll(selector).forEach((node) => {
        const key = node.getAttribute('href') || node.textContent.trim();
        if (!key || seen.has(key)) return;
        seen.add(key);
        nodes.push(node);
      });
    });
    return nodes;
  };

  const buildResults = (rendered, raw, canonical, showIntent) => {
    const products = uniqueNodes(rendered, '.jp-so__prod[data-jp-so-item]');
    const content = uniqueNodes(rendered, '.jp-so__link[data-jp-so-item]');
    if (!products.length && !content.length) return null;

    renderSequence += 1;
    const listboxId = 'jp-so-results-' + renderSequence;
    const wrapper = document.createElement('div');
    const shownProducts = products.slice(0, PRODUCT_LIMIT);
    const shownContent = content.slice(0, CONTENT_LIMIT);
    const shownTotal = shownProducts.length + shownContent.length;
    const number = new Intl.NumberFormat(cfg.locale || document.documentElement.lang || 'en').format(shownTotal);
    const count = document.createElement('div');
    count.className = 'jp-so__count';
    count.setAttribute('aria-hidden', 'true');
    count.textContent = number + ' ' + (cfg.strings.results || 'risultati');
    wrapper.appendChild(count);

    if (showIntent && canonical.toLocaleLowerCase() !== raw.toLocaleLowerCase()) {
      const intent = document.createElement('div');
      intent.className = 'jp-so__intent';
      intent.innerHTML = '<span>' + esc(cfg.strings.alsoSearched || 'Altri termini cercati') + '</span>' +
        '<button type="button" data-jp-soq="' + esc(canonical) + '">' + esc(canonical) + '</button>';
      wrapper.appendChild(intent);
    }

    const grid = document.createElement('div');
    grid.className = 'jp-so__grid';
    grid.id = listboxId;
    grid.setAttribute('role', 'listbox');
    grid.setAttribute('aria-label', cfg.strings.resultsLabel || cfg.strings.results || 'Risultati della ricerca');

    const appendColumn = (nodes, sourceSelector, fallbackLabel, prefix, hasMore) => {
      if (!nodes.length) return;
      const sourceColumn = rendered.map((root) => root.querySelector(sourceSelector)?.closest('.jp-so__col')).find(Boolean);
      const column = document.createElement('div');
      column.className = 'jp-so__col';
      column.setAttribute('role', 'group');
      const label = sourceColumn ? sourceColumn.getAttribute('aria-label') : fallbackLabel;
      column.setAttribute('aria-label', label || fallbackLabel);
      const heading = sourceColumn && sourceColumn.querySelector('.jp-so__h')
        ? sourceColumn.querySelector('.jp-so__h').cloneNode(true)
        : document.createElement('div');
      heading.className = 'jp-so__h';
      heading.setAttribute('role', 'presentation');
      heading.setAttribute('aria-hidden', 'true');
      if (!heading.textContent.trim()) heading.textContent = fallbackLabel;
      const badge = document.createElement('span');
      badge.className = 'jp-so__h-count';
      badge.textContent = nodes.length + (hasMore ? '+' : '');
      heading.appendChild(badge);
      column.appendChild(heading);
      nodes.forEach((source, index) => {
        const node = source.cloneNode(true);
        node.id = 'jpso-' + prefix + renderSequence + '-' + (index + 1);
        node.setAttribute('role', 'option');
        node.setAttribute('aria-selected', 'false');
        node.classList.remove('is-active');
        column.appendChild(node);
      });
      grid.appendChild(column);
    };

    appendColumn(shownProducts, '.jp-so__prod[data-jp-so-item]', cfg.strings.products || 'Forbici', 'p', products.length > PRODUCT_LIMIT);
    appendColumn(shownContent, '.jp-so__link[data-jp-so-item]', cfg.strings.guides || 'Guide e altro', 'g', content.length > CONTENT_LIMIT);
    wrapper.appendChild(grid);
    return { wrapper, listboxId, shownTotal };
  };

  const renderSuccess = (built, raw, canonical) => {
    showLive();
    liveEl.innerHTML = '';
    liveEl.appendChild(built.wrapper);
    input.removeAttribute('aria-busy');
    input.setAttribute('aria-controls', built.listboxId);
    highlight(liveEl, [raw, canonical]);
    if (statusEl) {
      const number = new Intl.NumberFormat(cfg.locale || document.documentElement.lang || 'en').format(built.shownTotal);
      statusEl.textContent = number + ' ' + (cfg.strings.results || 'risultati');
    }
    if (footEl) {
      footEl.hidden = false;
      footEl.innerHTML = '<a class="jp-so__seeall" data-jp-so-seeall="' + esc(raw) + '" href="' + esc(fullSearchUrl(raw)) + '">' +
        esc(cfg.strings.viewAll || 'Vedi tutti i risultati') + '<span aria-hidden="true"> →</span></a>';
    }
  };

  const onInput = () => {
    clearBtn.hidden = !input.value;
    input.removeAttribute('aria-activedescendant');
    window.clearTimeout(timer);
    if (composing) return;
    if (!input.value.trim()) { showEmpty(); return; }
    timer = window.setTimeout(runSearch, 180);
  };

  const runSearch = () => {
    const raw = input.value.trim();
    if (!raw) { showEmpty(); return; }
    const canonical = intentQuery(raw) || raw;
    const queries = canonical.toLocaleLowerCase() === raw.toLocaleLowerCase() ? [raw] : [raw, canonical];
    stopRequest();
    const sequence = ++requestSequence;
    skeleton();
    ctrl = new AbortController();
    let timedOut = false;
    timeout = window.setTimeout(() => { timedOut = true; if (ctrl) ctrl.abort(); }, REQUEST_TIMEOUT);

    Promise.allSettled(queries.map((query) => fetchPredictive(query, ctrl.signal)))
      .then((responses) => {
        if (sequence !== requestSequence) return;
        window.clearTimeout(timeout);
        const successful = responses.filter((response) => response.status === 'fulfilled');
        const rendered = successful.map((response) => new DOMParser().parseFromString(response.value, 'text/html').querySelector('[data-jp-so-rendered]')).filter(Boolean);
        const canonicalSucceeded = queries.length > 1 && responses[1]?.status === 'fulfilled';
        const built = buildResults(rendered, raw, canonical, canonicalSucceeded);
        if (built) renderSuccess(built, raw, canonical);
        else if (successful.length === responses.length && rendered.length === responses.length) noResults(raw, canonical);
        else searchUnavailable();
      })
      .catch(() => { if (sequence === requestSequence) searchUnavailable(); })
      .finally(() => {
        if (sequence !== requestSequence) return;
        window.clearTimeout(timeout);
        if (timedOut && input.getAttribute('aria-busy') === 'true') searchUnavailable();
        ctrl = null;
      });
  };

  const onKeydown = (event) => {
    if (composing || event.isComposing || event.keyCode === 229) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      if (window.JapanScissors && window.JapanScissors.layers) window.JapanScissors.layers.close('search');
      return;
    }
    const items = liveEl.hidden ? [] : Array.from(liveEl.querySelectorAll('[data-jp-so-item]'));
    if (event.key === 'Enter') {
      const active = liveEl.querySelector('[data-jp-so-item].is-active');
      if (active) {
        event.preventDefault();
        recordRecent(input.value);
        window.location.href = active.getAttribute('href');
      } else if (input.value.trim()) {
        event.preventDefault();
        recordRecent(input.value);
        trackSubmit(input.value.trim());
        window.location.href = fullSearchUrl(input.value.trim());
      }
      return;
    }
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && items.length) {
      event.preventDefault();
      input.setAttribute('aria-expanded', 'true');
      let index = items.findIndex((item) => item.classList.contains('is-active'));
      if (event.key === 'ArrowUp' && index <= 0) {
        items.forEach((item) => { item.classList.remove('is-active'); item.setAttribute('aria-selected', 'false'); });
        input.removeAttribute('aria-activedescendant');
        return;
      }
      items.forEach((item) => { item.classList.remove('is-active'); item.setAttribute('aria-selected', 'false'); });
      index = event.key === 'ArrowDown' ? (index + 1) % items.length : index - 1;
      const current = items[index];
      current.classList.add('is-active');
      current.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', current.id);
      current.scrollIntoView({ block: 'nearest' });
    }
  };

  document.addEventListener('jp:layer:open', (event) => {
    if (event.detail.id !== 'search' || !overlay || !input) return;
    input.value = '';
    clearBtn.hidden = true;
    showEmpty();
    if (overlay.querySelector('.jp-so__body')) overlay.querySelector('.jp-so__body').scrollTop = 0;
  });
  document.addEventListener('jp:layer:close', (event) => { if (event.detail.id === 'search') stopRequest(); });

  const boot = () => { const element = document.querySelector('[data-jp-layer="search"]'); if (element) init(element); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('jp:privacy:change', () => { if (recentEl) renderRecent(); });
})();
